import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {API_CONFIG, ERROR_MESSAGES} from '../config';
import {
    DEFAULT_PROFILES,
    mergeProfileSettings,
    randColor,
} from '../constants';
import {profileService} from '../services/profiles/profileService';

const normalizeProfile = (profile, {persisted = false, isFallback = false} = {}) => {
    const settings = mergeProfileSettings(profile?.settings || profile?.profileSettings || {});
    const color = profile?.color || settings.color || randColor();

    return {
        id: String(profile?.id || profile?.profileId || `profile-${profile?.name || Date.now()}`),
        name: profile?.name || profile?.profileName || 'Untitled profile',
        color,
        persisted,
        isFallback,
        settings: {
            ...settings,
            color,
        },
    };
};

export const useProfiles = (accountId) => {
    const fallbackProfiles = useMemo(
        () =>
            DEFAULT_PROFILES.map((profile) =>
                normalizeProfile(profile, {persisted: false, isFallback: true}),
            ),
        [],
    );
    const [profiles, setProfiles] = useState(fallbackProfiles);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [source, setSource] = useState('local');
    const abortRef = useRef(null);

    const syncProfiles = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        if (!API_CONFIG.ENABLE_PROFILE_SYNC || !accountId) {
            setProfiles(fallbackProfiles);
            setSource('local');
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const remoteProfiles = await profileService.getProfiles(accountId);
            if (controller.signal.aborted) return;
            if (!remoteProfiles.length) {
                setProfiles(fallbackProfiles);
                setSource('local');
                return;
            }

            setProfiles(
                remoteProfiles.map((profile) =>
                    normalizeProfile(profile, {persisted: true, isFallback: false}),
                ),
            );
            setSource('remote');
        } catch (profileError) {
            if (controller.signal.aborted) return;
            setProfiles(fallbackProfiles);
            setSource('local');
            setError(profileError.message || ERROR_MESSAGES.PROFILE_LOAD_FAILED);
        } finally {
            if (!controller.signal.aborted) setIsLoading(false);
        }
    }, [accountId, fallbackProfiles]);

    useEffect(() => {
        syncProfiles();
        return () => abortRef.current?.abort();
    }, [syncProfiles]);

    const saveProfile = useCallback(
        async (profileDraft) => {
            const normalizedSettings = {
                ...mergeProfileSettings(profileDraft?.settings || {}),
                color: profileDraft?.color || profileDraft?.settings?.color || randColor(),
            };
            const payload = {
                accountId,
                name: profileDraft?.name?.trim() || 'Untitled profile',
                settings: normalizedSettings,
            };
            const shouldUpdate = Boolean(profileDraft?.persisted && !profileDraft?.isFallback);
            const savedProfile = shouldUpdate
                ? await profileService.updateProfile(profileDraft.id, payload)
                : await profileService.createProfile(payload);
            const normalizedProfile = normalizeProfile(
                {
                    ...savedProfile,
                    name: savedProfile?.name || payload.name,
                    settings: savedProfile?.settings || payload.settings,
                },
                {persisted: true, isFallback: false},
            );

            setProfiles((currentProfiles) => {
                const index = currentProfiles.findIndex((profile) => profile.id === profileDraft?.id);

                if (index >= 0) {
                    return currentProfiles.map((profile, currentIndex) =>
                        currentIndex === index ? normalizedProfile : profile,
                    );
                }

                return [...currentProfiles, normalizedProfile];
            });
            setSource('remote');
            setError(null);

            return normalizedProfile;
        },
        [accountId],
    );

    const deleteProfile = useCallback(async (profileToDelete) => {
        if (!profileToDelete) return false;

        if (profileToDelete.persisted && !profileToDelete.isFallback) {
            await profileService.deleteProfile(profileToDelete.id);
        }

        setProfiles((currentProfiles) =>
            currentProfiles.filter((profile) => profile.id !== profileToDelete.id),
        );
        setError(null);
        return true;
    }, []);

    return {
        profiles,
        isLoading,
        error,
        source,
        syncProfiles,
        saveProfile,
        deleteProfile,
    };
};
